import { useRef, useState, Dispatch, SetStateAction } from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';
import { getApi } from '../../providers/AuthContext';
import { Camera, Upload, Loader2, CheckCircle2 } from 'lucide-react';

interface PapImageUploadProps {
  formBinding: UseFormRegisterReturn;
  setFormValue: (name: string, value: any) => void;
  imageUrlState: [string, Dispatch<SetStateAction<string>>];
}

const PapImageUpload: React.FC<PapImageUploadProps> = ({ formBinding, setFormValue, imageUrlState }) => {
  const [imageUrl, setImageUrl] = imageUrlState;
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const api = getApi();

  const pickFile = (e: React.MouseEvent) => {
    e.preventDefault();
    fileRef.current?.click();
  };

  const fileChanged = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError(null);
    setUploadSuccess(false);
    setUploading(true);
    const data = new FormData();
    data.append('file', file);

    try {
      const result = await api.uploadFile('/api/v1/files', data);
      if (fileRef.current) fileRef.current.value = '';
      setImageUrl(result.url);
      setFormValue('image_id', result.id);
      setUploadSuccess(true);
      setTimeout(() => setUploadSuccess(false), 2000);
    } catch {
      setUploadError('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mb-5">
      <label className="form-label">
        Profile Image
      </label>

      {/* Image preview with overlay upload trigger */}
      <div className="relative group w-fit">
        <div className="relative w-[180px] h-[180px] sm:w-[220px] sm:h-[220px] rounded-full overflow-hidden border-4 border-border/30 shadow-soft-md group-hover:shadow-soft-lg transition-shadow duration-300">
          <img
            src={imageUrl}
            alt="Profile"
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          {/* Hover overlay */}
          <button
            onClick={pickFile}
            disabled={uploading}
            className="absolute inset-0 flex flex-col items-center justify-center bg-black/0 group-hover:bg-black/40 transition-all duration-200 cursor-pointer"
          >
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col items-center">
              {uploading ? (
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              ) : uploadSuccess ? (
                <>
                  <CheckCircle2 className="w-8 h-8 text-white drop-shadow-md" />
                  <span className="text-white text-xs font-medium mt-1.5 drop-shadow-md">Uploaded!</span>
                </>
              ) : (
                <>
                  <Camera className="w-8 h-8 text-white drop-shadow-md" />
                  <span className="text-white text-xs font-medium mt-1.5 drop-shadow-md">Change Photo</span>
                </>
              )}
            </div>
          </button>
        </div>

        {/* Gradient ring effect */}
        <div className="absolute -inset-1 rounded-full bg-gradient-to-tr from-primary via-accent to-secondary opacity-20 group-hover:opacity-35 -z-10 blur-sm transition-opacity duration-300" />
      </div>

      {/* Upload status */}
      {uploading && (
        <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          <span>Uploading...</span>
        </div>
      )}

      {/* Upload error */}
      {uploadError && (
        <p className="mt-3 text-sm text-destructive flex items-center gap-1.5">
          {uploadError}
        </p>
      )}

      {/* Alternative upload button (always visible on mobile) */}
      <button
        onClick={pickFile}
        disabled={uploading}
        className="mt-4 btn-light py-2 px-4 rounded-xl text-sm gap-2 disabled:opacity-50"
      >
        {uploading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Uploading...
          </>
        ) : (
          <>
            <Upload className="w-4 h-4" />
            Upload Image
          </>
        )}
      </button>

      <input onChange={fileChanged} accept="image/*" ref={fileRef} aria-label="Upload profile image" type="file" className="hidden" />
      <input {...formBinding} type="hidden" name="image_id" id="image_id" />
    </div>
  );
};

export default PapImageUpload;
